import Decimal from "decimal.js-light";
import { has } from "lodash";
import { evaluate } from "mathjs";
import { formatPhoneNumberIntl } from "react-phone-number-input";

import { getTextColor } from "@/utils/color";
import {
  PRICING_STRATEGY_BASIC_QUANTITIES_LABEL,
  PRICING_STRATEGY_CUSTOM_FORMULA_LABEL,
  PRICING_STRATEGY_LINEAL_FEET_LABEL,
  PRICING_STRATEGY_LINEAL_METRES_LABEL,
  PRICING_STRATEGY_SQUARE_FEET_LABEL,
  PRICING_STRATEGY_SQUARE_METRES_LABEL,
  PRODUCT_TYPE_ADDITIONAL_PRODUCT_LABEL,
  PRODUCT_TYPE_LABOUR_PRODUCT_LABEL,
  PRODUCT_TYPE_NOTES_ITEM_LABEL,
  PRODUCT_TYPE_ON_THE_FLY_PRODUCT_LABEL,
  PRODUCT_TYPE_PRODUCT_KIT_LABEL,
} from "@/utils/constants";
import { getCountryByCode, getStateByCode } from "@/utils/countries-with-state";
import * as date from "@/utils/date";
import {
  formatSquareMetreDimensionForDisplay,
  formatTotalAreaInSquareFeetForDisplay,
  formatTotalAreaInSquareMetresForDisplay,
  formatTotalLinealFeetForDisplay,
  formatTotalLinealMetresForDisplay,
  normalizeFeetInches,
} from "@/utils/measurement/measurement-for-display";
import { formatPrice } from "@/utils/prices";

const isVisibleInPdfMap = {
  INVOICE: "displayOnInvoicePdf",
  WORK_ORDER: "displayOnWorkOrderPdf",
  PARTIAL_INVOICE: "displayOnInvoicePdf",
  QUOTE: "displayOnQuotePdf",
  PROFORMA_INVOICE: "displayOnProformaInvoicePdf",
  DELIVERY_DOCKET: "displayOnDeliveryDocketPdf",
};

const isHideInPdfMap = {
  INVOICE: "isHideInvoicePdf",
  WORK_ORDER: "isHideWorkOrderPdf",
  PARTIAL_INVOICE: "isHideInvoicePdf",
  QUOTE: "isHideQuotePdf",
  PURCHASE_ORDER: "isHidePurchaseOrderPdf",
  PROFORMA_INVOICE: "isHideProformaInvoicePdf",
  DELIVERY_DOCKET: "isHideDeliveryDocketPdf",
};

const isVisibleInPdf = ({ displayOptions, templateType }) => {
  if (!Array.isArray(displayOptions)) {
    return false;
  }

  return displayOptions.includes(isVisibleInPdfMap[templateType]);
};

const isHiddenInPdf = ({ fields, templateType }) => {
  // Return true when the field is marked as hidden; default to not hidden (false) if key is absent
  return has(fields, isHideInPdfMap[templateType]) ? !!fields[isHideInPdfMap[templateType]] : false;
};

const formatAddress = ({
  address1,
  address2,
  city,
  stateCode,
  countryCode,
  postcode,
  hasDrawings = false,
}) => {
  // if the address is for PDF with drawings, append country on the last line
  if (hasDrawings) {
    return [
      address1 || "",
      address2 || "",
      [
        city || "",
        getStateByCode({ countryCode: countryCode || "", stateCode: stateCode || "" })?.name ?? "",
        postcode || "",
        getCountryByCode(countryCode || "")?.name ?? "",
      ]
        .filter((part) => part !== null && part !== "")
        .join(", "),
    ]
      .filter((line) => line !== null && line !== "")
      .join("\n");
  }

  // if the address is for PDF without drawings, add country on a new line
  return [
    address1 || "",
    address2 || "",
    [
      city || "",
      getStateByCode({ countryCode: countryCode || "", stateCode: stateCode || "" })?.name ?? "",
      postcode || "",
    ]
      .filter((part) => part !== null && part !== "")
      .join(", "),
    getCountryByCode(countryCode || "")?.name ?? "",
  ]
    .filter((line) => line !== null && line !== "")
    .join("\n");
};

const getLineItem = ({ product, templateType, company }) => {
  const currencySymbol = company?.currencySymbol ?? "$";
  const baseItem = {
    is_completed: product?.isCompleted ?? false,
    product_type: product.productType,
    pricing_strategy: product.pricingStrategy
      ? product.pricingStrategy.replace(" ", "_").toLowerCase()
      : "",
    name: product.name || product.productName,
    description: product.productDescription,
    price: product.value ? formatPrice({ price: parseFloat(product.value), currencySymbol }) : "",
    total: product.totalValue
      ? formatPrice({
          price: parseFloat(product.totalValue),
          currencySymbol,
        })
      : "",
    quantity: product.quantity,
    types: [
      ...(product.types && Array.isArray(product.types)
        ? product.types.map((type) => ({
            ...type,
            label: type.name,
            value: type.value,
          }))
        : []),
    ],
  };

  if (
    [
      PRODUCT_TYPE_ADDITIONAL_PRODUCT_LABEL,
      PRODUCT_TYPE_PRODUCT_KIT_LABEL,
      PRODUCT_TYPE_ON_THE_FLY_PRODUCT_LABEL,
    ].includes(product.productType)
  ) {
    if (product.pricingStrategy === PRICING_STRATEGY_BASIC_QUANTITIES_LABEL) {
      return baseItem;
    }
    if (product.pricingStrategy === PRICING_STRATEGY_LINEAL_METRES_LABEL) {
      const lengths = product?.subitems ?? product?.lengthItems ?? [];
      const lengthTotal = formatTotalLinealMetresForDisplay({ items: lengths });
      return {
        ...baseItem,
        quantity: lengths.length > 0 ? `${lengthTotal} m` : "",
        lengths: lengths.map((s) => ({
          amount: s.amount,
          length: `${parseFloat(s.length || 0)} mm`,
        })),
      };
    }
    if (product.pricingStrategy === PRICING_STRATEGY_SQUARE_METRES_LABEL) {
      const lengths = product?.subitems ?? product?.lengthItems ?? [];
      const sqmTotal = formatTotalAreaInSquareMetresForDisplay({ items: lengths });

      return {
        ...baseItem,
        quantity: lengths.length > 0 ? `${sqmTotal} sqm` : "",
        dimensions: lengths.map((s) => ({
          amount: s?.amount ?? 1,
          length: formatSquareMetreDimensionForDisplay({
            value: s.length || 0,
            unit: s.displayUnit,
          }),
          width: formatSquareMetreDimensionForDisplay({ value: s.width || 0, unit: s.displayUnit }),
        })),
      };
    }

    if (product.pricingStrategy === PRICING_STRATEGY_LINEAL_FEET_LABEL) {
      const lengths = product?.subitems ?? product?.lengthItems ?? [];
      const lengthTotal = formatTotalLinealFeetForDisplay({ items: lengths });

      return {
        ...baseItem,
        quantity:
          lengths.length > 0 ? `${lengthTotal.feetDisplay} ft ${lengthTotal.inchesDisplay} in` : "",
        lengths: lengths.map((s) => {
          const { feetDisplay, inchesDisplay } = normalizeFeetInches({
            feet: s.lengthFt,
            inches: s.lengthIn,
          });
          return {
            amount: s.amount,
            length: `${feetDisplay} ft ${inchesDisplay} in`,
          };
        }),
      };
    }

    if (product.pricingStrategy === PRICING_STRATEGY_SQUARE_FEET_LABEL) {
      const lengths = product?.subitems ?? product?.lengthItems ?? [];
      const sqfTotal = formatTotalAreaInSquareFeetForDisplay({ items: lengths });
      return {
        ...baseItem,
        quantity: lengths.length > 0 ? `${sqfTotal} sq ft` : "",
        dimensions: lengths.map((s) => {
          const { feetDisplay: lengthFeet, inchesDisplay: lengthInches } = normalizeFeetInches({
            feet: s.lengthFt,
            inches: s.lengthIn,
          });
          const { feetDisplay: widthFeet, inchesDisplay: widthInches } = normalizeFeetInches({
            feet: s.widthFt,
            inches: s.widthIn,
          });
          return {
            amount: s?.amount ?? 1,
            length: `${lengthFeet} ft ${lengthInches} in`,
            width: `${widthFeet} ft ${widthInches} in`,
          };
        }),
      };
    }
    if (product.pricingStrategy === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL) {
      const formula = product.customFormula.elements.map((e) => e.value).join("");
      const output = evaluate(formula);
      const answer =
        Number.isNaN(output) || output === Infinity
          ? ""
          : product.customFormula.isRoundedToNearestWholeNumber
            ? Math.round(output)
            : Decimal(output).toDecimalPlaces(4).toFixed(2);

      const customFormulaElements = product.customFormula.elements
        .filter((e) => e.type === "variable")
        .map((e) => ({
          label: e.name,
          value: e.value ? e.value.match(/\d+/g).join("") : "",
        }));

      return {
        ...baseItem,
        custom_formula: isVisibleInPdf({
          displayOptions: product.customFormula.displayFormula,
          templateType,
        })
          ? customFormulaElements
          : "",
        custom_formula_answer: isVisibleInPdf({
          displayOptions: product.customFormula.displayFormula,
          templateType,
        })
          ? answer
          : "",
      };
    }
  } else if (product.productType === PRODUCT_TYPE_NOTES_ITEM_LABEL) {
    return {
      ...baseItem,
      name: product.notes,
      description: product.productDescription,
      price: "",
      total: "",
      quantity: "Note",
    };
  } else if (product.productType === PRODUCT_TYPE_LABOUR_PRODUCT_LABEL) {
    return {
      ...baseItem,
      pricing_strategy: "",
      name: product?.name ?? product.productName ?? "",
      description: product.productDescription,
    };
  }
};

const getLineItemColour = (data) => {
  return data.find((pa) => pa.name === "Colour" && pa.value !== "")?.value ?? "";
};

const getCompanyData = ({ company, hasDrawings = false }) => ({
  tax_label: company?.taxLabel || "Tax",
  company_logo: company.image || "",
  company_name: company?.companyName || "",
  company_email: company?.email || "",
  company_phone: company?.phoneNumber ? formatPhoneNumberIntl(company?.phoneNumber) : "",
  company_abn: company?.abn || "",
  company_address: formatAddress({
    address1: company?.billingAddress1,
    address2: company?.billingAddress2,
    city: company?.billingCity,
    stateCode: company?.billingState,
    countryCode: company?.billingCountryCode,
    postcode: company?.billingPostcode,
    hasDrawings,
  }),
});

// Generic for Sales Order and Purchase Order
const getGenericOrderData = ({ order, templateType, company }) => {
  const currencySymbol = company?.currencySymbol ?? "$";
  const accountingNumberMap = {
    INVOICE: order?.accountingInvoiceNumber || "",
    PARTIAL_INVOICE: order?.accountingInvoiceNumber || "",
    WORK_ORDER: order?.accountingInvoiceNumber || "",
    QUOTE: order?.accountingQuoteNumber || "",
    PROFORMA_INVOICE: order?.accountingQuoteNumber || "",
    DELIVERY_DOCKET: order?.accountingInvoiceNumber || "",
    PURCHASE_ORDER: order?.accountingPurchaseOrderNumber || "",
    RUN_SHEET: order?.accountingInvoiceNumber || "",
  };

  return {
    current_date: date.toLocalDateTime({
      date: new Date(),
      timezone: date.getCurrentUserTimezone(),
      format: date.getCompanyDateFormat(),
    }),
    accounting_number: accountingNumberMap[templateType] || "",
    contact_name: order?.contact?.contactName || "",
    contact_email: order?.contact?.email || "",
    contact_phone_number: order?.contact?.phone ? formatPhoneNumberIntl(order?.contact?.phone) : "",
    contact_mobile_number: order?.contact?.mobile
      ? formatPhoneNumberIntl(order?.contact?.mobile)
      : "",
    notes: order?.notes || "",
    deliver_to: formatAddress({
      address1: order?.shippingAddress?.address1,
      address2: order?.shippingAddress?.address2,
      city: order?.shippingAddress?.city,
      stateCode: order?.shippingAddress?.state,
      countryCode: order?.shippingAddress?.countryCode,
      postcode: order?.shippingAddress?.postcode,
    }),
    install_at: formatAddress({
      address1: order?.installAddress?.address1,
      address2: order?.installAddress?.address2,
      city: order?.installAddress?.city,
      stateCode: order?.installAddress?.state,
      countryCode: order?.installAddress?.countryCode,
      postcode: order?.installAddress?.postcode,
    }),
    delivery_fee:
      order.deliveryFee && parseFloat(order.deliveryFee) !== 0
        ? formatPrice({
            price: parseFloat(order.deliveryFee),
            currencySymbol,
          })
        : "",
    discounts: order.discounts,
    sub_total_value: order?.subTotal
      ? formatPrice({
          price: parseFloat(order.subTotal).toFixed(2),
          currencySymbol,
        })
      : "",
    gst_value: order?.gst
      ? formatPrice({
          price: parseFloat(order.gst).toFixed(2),
          currencySymbol,
        })
      : "",
    total_value: order?.totalPrice
      ? formatPrice({
          price: parseFloat(order.totalPrice).toFixed(2),
          currencySymbol,
        })
      : "",
  };
};

const getCustomFieldsData = ({ customFields, currencySymbol }) => {
  return customFields.map((field) => {
    if (field.column_type === "currency") {
      return {
        ...field,
        value: formatPrice({ price: parseFloat(field.value), currencySymbol }),
      };
    }
    return {
      ...field,
    };
  });
};

const getSalesOrderBaseData = ({ order, customer }) => ({
  created_date: order?.createdDate || "",
  required_date: order?.requiredDate || "",
  customer_name: order?.customerName || "",
  customer_abn: customer?.abn || "",
  po: order?.reference || "",
  obtaining_method: order?.obtainingMethod || "",
  pickup_notes: order?.pickupNotes || "",
  order_number: order?.purchaseOrder ? (`#${order?.purchaseOrder}` ?? "") : "",
  customer_address: formatAddress({
    address1: order?.billingAddress?.address1,
    address2: order?.billingAddress?.address2,
    city: order?.billingAddress?.city,
    stateCode: order?.billingAddress?.state,
    countryCode: order?.billingAddress?.countryCode,
    postcode: order?.billingAddress?.postcode,
  }),
  // The order's labels for the Labels field on PDF templates.
  // `color` is the text colour on the label, `backgroundColor` is the label's own colour.
  labels: (order?.labels || []).map((label) => ({
    name: label.name,
    color: getTextColor({ hex: label.colour }),
    backgroundColor: label.colour,
  })),
});

export const getDataTemplate = ({
  order,
  products = [],
  drawings = [],
  drawingsSvgs = [],
  customFields = [],
  company,
  customer = null,
  templateType,
}) => {
  const currencySymbol = company?.currencySymbol ?? "$";
  const getProductsData = () => ({
    product_table: [
      ...drawings.map((d) => ({
        index: d.index,
        product_type: d.productType,
        // girth: d.girth ? `Girth ${d.girth}mm` : "",
        svg_url: d.svgUrl,
        near_side_girth: d.nearSideGirth ? `Girth ${d.nearSideGirth}mm` : "",
        near_side_svg_url: d.nearSideSvgUrl,
        far_side_girth: d.farSideGirth ? `Girth ${d.farSideGirth}mm` : "",
        far_side_svg_url: d.farSideSvgUrl,
        name: d.name ? `${d.isTapered ? "Tapered " : ""} ${d.name}` : "",
        thickness: d.thickness ? d.thickness : "",
        girth: d.girth ? `${d.girth}mm` : "",
        bends: d.bends ? `${d.bends} bends` : "",
        lengths: d.subitems.map((s) => ({
          amount: s.amount,
          length: `${s.length}mm`,
        })),
        total_length: `${d.totalLength}m`,
        price: d.value ? formatPrice({ price: parseFloat(d.value), currencySymbol }) : "",
        total: d.totalValue ? formatPrice({ price: parseFloat(d.totalValue), currencySymbol }) : "",
        size: d.size,
      })),
      ...products.map((p) => {
        if (p.productType === "Product Kit") {
          const baseKit = {
            name: p?.name ?? p.productName ?? "",
            description: p.productKitDescription,
            price: p.value ? formatPrice({ price: parseFloat(p.value), currencySymbol }) : "",
            total: p.totalValue
              ? formatPrice({ price: parseFloat(p.totalValue), currencySymbol })
              : "",
            product_type: p.productType,
            quantity: p.quantity ? parseFloat(p.quantity) : "",
            is_completed: p?.isCompleted ?? false,
            types:
              p.types && Array.isArray(p.types)
                ? p.types
                    .filter((t) => !isHiddenInPdf({ fields: t, templateType }))
                    .map((t) => ({
                      label: t.name,
                      value: t.value,
                    }))
                : [],
            kit_items: isVisibleInPdf({ displayOptions: p.displayKitItems, templateType })
              ? p.productKitItems.map((k) => {
                  const item = getLineItem({ product: k, templateType, company });
                  return {
                    ...item,
                    types: [
                      ...(k.productRow?.material || k.productRow?.colour
                        ? [
                            {
                              label: k.productRow.material,
                              value: k.productRow.colour,
                            },
                          ]
                        : []),
                      ...item.types.filter((t) => !isHiddenInPdf({ fields: t, templateType })),
                    ],
                  };
                })
              : [],
            sub_kits: isVisibleInPdf({ displayOptions: p.displaySubKits, templateType })
              ? p.subKits.map((s) => ({
                  name: s.title,
                  description: "",
                  product_type: p.productType,
                  quantity: s.quantity ? parseFloat(s.quantity) : "",
                  price: s.value ? formatPrice({ price: parseFloat(s.value), currencySymbol }) : "",
                  total: s.totalValue
                    ? formatPrice({ price: parseFloat(s.totalValue), currencySymbol })
                    : "",
                  is_completed: s?.isCompleted ?? false,
                  kit_items: s.kitProducts.map((k) => {
                    const item = getLineItem({ product: k, templateType, company });
                    return {
                      ...item,
                      types: [
                        ...(k.productRow?.material || k.productRow?.colour
                          ? [
                              {
                                label: k.productRow.material,
                                value: k.productRow.colour,
                              },
                            ]
                          : []),
                        ...item.types.filter((t) => !isHiddenInPdf({ fields: t, templateType })),
                      ],
                    };
                  }),
                }))
              : [],
          };
          return baseKit;
        }
        const item = getLineItem({ product: p, templateType, company });
        return {
          ...item,
          types: [
            {
              label: p.materialName,
              value: getLineItemColour(p?.params || []),
            },
            ...item.types.filter((t) => !isHiddenInPdf({ fields: t, templateType })),
          ],
        };
      }),
    ],
  });

  const getDrawingsSvgsData = () =>
    drawingsSvgs.map((d) => ({
      index: d.index,
      product_type: d.productType,
      girth: d.girth ? `Girth ${d.girth}` : "",
      svg_url: d.svgUrl,
      svg_file: d?.svgFile ?? "",
      near_side_girth: d.nearSideGirth ? `Girth ${d.nearSideGirth}` : "",
      near_side_svg_url: d.nearSideSvgUrl,
      near_side_svg_file: d?.nearSideSvgFile ?? "",
      far_side_girth: d.farSideGirth ? `Girth ${d.farSideGirth}` : "",
      far_side_svg_url: d.farSideSvgUrl,
      far_side_svg_file: d?.farSideSvgFile ?? "",
      name: d.name ? d.name : "",
      thickness: d.thickness ? d.thickness : "",
      bends: d.bends ? `${d.bends} bends` : "",
      lengths: d.subitems.map((s) => ({
        amount: s.amount,
        length: `${s.length}mm`,
      })),
      total_length: `${d.totalLength}m`,
      price: d.value ? formatPrice({ price: parseFloat(d.value), currencySymbol }) : "",
      total: d.totalValue ? formatPrice({ price: parseFloat(d.totalValue), currencySymbol }) : "",
      custom_total: d.customTotalValue,
      size: d.size,
      is_completed: d?.isCompleted ?? false,
    }));

  const getPurchaseOrderBaseData = () => ({
    supplier_abn: order?.supplierAbn || "",
    supplier_name: order?.supplierName || "",
    supplier_address: formatAddress({
      address1: order?.supplierAddress?.address1,
      address2: order?.supplierAddress?.address2,
      city: order?.supplierAddress?.city,
      stateCode: order?.supplierAddress?.state,
      countryCode: order?.supplierAddress?.countryCode,
      postcode: order?.supplierAddress?.postcode,
    }),
    billing_address: formatAddress({
      address1: order?.billingAddress?.address1,
      address2: order?.billingAddress?.address2,
      city: order?.billingAddress?.city,
      stateCode: order?.billingAddress?.state,
      countryCode: order?.billingAddress?.countryCode,
      postcode: order?.billingAddress?.postcode,
    }),
    reference: order?.reference || "",
    purchase_order_number: order?.purchaseOrderNumber
      ? (`#${order?.purchaseOrderNumber}` ?? "")
      : "",
    pickup_notes: order?.pickupNotes || "",
  });

  const getDataByTemplateType = (templateType) => {
    if (templateType === "INVOICE") {
      return {
        ...getGenericOrderData({ order, templateType, company }),
        ...getCompanyData({ company }),
        ...getProductsData(),
        ...getSalesOrderBaseData({ order, customer }),
        custom_fields: getCustomFieldsData({ customFields, currencySymbol }),
        accounting_name:
          company?.accountingName && company.accountingName !== "None"
            ? (`${company?.accountingName} #:` ?? "")
            : "",
        custom_message: order.customMessage,
        invoice_date: order?.invoiceDate || "",
        due_date: order?.invoiceDueDate || "",
        tax_invoice_number: order?.taxInvoiceNumber ? `#${order?.taxInvoiceNumber}` : "",
        company_bsb: customer?.bsb || company?.bsb || "",
        company_bank_account_number:
          customer?.bankAccountNumber || company?.bankAccountNumber || "",
        company_bank_account_name: customer?.bankAccountName || company?.bankAccountName || "",
        ...(order.paymentDetails && {
          payment_details: {
            invoice_url: order.paymentDetails.invoiceUrl || "",
            is_stripe_enabled: order.paymentDetails.isStripeEnabled ?? false,
          },
        }),
      };
    }

    if (templateType === "PARTIAL_INVOICE") {
      const invoicedToDate = parseFloat(order.invoicedToDate);
      const remainingToBeInvoiced = parseFloat(order.remainingToBeInvoiced);
      return {
        ...getGenericOrderData({ order, templateType, company }),
        ...getCompanyData({ company }),
        ...getProductsData(),
        ...getSalesOrderBaseData({ order, customer }),
        custom_fields: getCustomFieldsData({ customFields, currencySymbol }),
        accounting_name:
          company?.accountingName && company.accountingName !== "None"
            ? (`${company?.accountingName} #:` ?? "")
            : "",
        custom_message: order.customMessage,
        invoice_date: order?.invoiceDate || "",
        due_date: order?.invoiceDueDate || "",
        tax_invoice_number: order?.taxInvoiceNumber ? `#${order?.taxInvoiceNumber}` : "",
        company_bsb: customer?.bsb || company?.bsb || "",
        company_bank_account_number:
          customer?.bankAccountNumber || company?.bankAccountNumber || "",
        company_bank_account_name: customer?.bankAccountName || company?.bankAccountName || "",
        invoiced_to_date:
          invoicedToDate >= 0
            ? formatPrice({ price: invoicedToDate.toFixed(2), currencySymbol })
            : `${currencySymbol}0`,
        remaining_to_be_invoiced:
          remainingToBeInvoiced >= 0
            ? `${
                remainingToBeInvoiced.toFixed(2) > 0
                  ? formatPrice({ price: remainingToBeInvoiced.toFixed(2), currencySymbol })
                  : 0
              }`
            : `${currencySymbol}0`,
        ...(order.paymentDetails && {
          payment_details: {
            invoice_url: order.paymentDetails.invoiceUrl || "",
            is_stripe_enabled: order.paymentDetails.isStripeEnabled ?? false,
          },
        }),
      };
    }

    if (templateType === "QUOTE") {
      return {
        ...getGenericOrderData({ order, templateType, company }),
        ...getCompanyData({ company, hasDrawings: true }),
        ...getProductsData(),
        ...getSalesOrderBaseData({ order, customer }),
        custom_fields: getCustomFieldsData({ customFields, currencySymbol }),
        accounting_name:
          company?.accountingName && company.accountingName !== "None"
            ? (`${company?.accountingName} #:` ?? "")
            : "",
        custom_message: order.customMessage,
        quote_number: order?.quoteNumber ? `#${order?.quoteNumber}` : "",
        quoted_date: order?.quotedDate || "",
        company_bsb: company?.bsb || "",
        company_bank_account_number: company?.bankAccountNumber || "",
        company_bank_account_name: company?.bankAccountName || "",
        drawings_svgs_data: getDrawingsSvgsData(),
      };
    }

    if (templateType === "PROFORMA_INVOICE") {
      return {
        ...getGenericOrderData({ order, templateType, company }),
        ...getCompanyData({ company }),
        ...getProductsData(),
        ...getSalesOrderBaseData({ order, customer }),
        custom_fields: getCustomFieldsData({ customFields, currencySymbol }),
        custom_message: order.customMessage,
        invoice_date: order?.invoiceDate || "",
        proforma_invoice_number: order?.proformaInvoiceNumber
          ? `#${order?.proformaInvoiceNumber}`
          : "",
        company_bsb: customer?.bsb || company?.bsb || "",
        company_bank_account_number:
          customer?.bankAccountNumber || company?.bankAccountNumber || "",
        company_bank_account_name: customer?.bankAccountName || company?.bankAccountName || "",
      };
    }

    if (templateType === "WORK_ORDER") {
      return {
        ...getGenericOrderData({ order, templateType, company }),
        ...getCompanyData({ company, hasDrawings: true }),
        ...getProductsData(),
        ...getSalesOrderBaseData({ order, customer }),
        custom_fields: getCustomFieldsData({ customFields, currencySymbol }),
        created_date: order?.createdDate || "",
        accounting_name:
          company?.accountingName && company.accountingName !== "None"
            ? (`${company?.accountingName} Order #` ?? "")
            : "",
        required_date: order?.requiredDate || "",
        drawings_svgs_data: getDrawingsSvgsData(),
      };
    }

    if (templateType === "DELIVERY_DOCKET") {
      return {
        ...getGenericOrderData({ order, templateType, company }),
        ...getCompanyData({ company, hasDrawings: true }),
        ...getProductsData(),
        ...getSalesOrderBaseData({ order, customer }),
        custom_fields: getCustomFieldsData({ customFields, currencySymbol }),
        created_date: order?.createdDate || "",
        accounting_name:
          company?.accountingName && company.accountingName !== "None"
            ? (`${company?.accountingName} Order #` ?? "")
            : "",
        required_date: order?.requiredDate || "",
        drawings_svgs_data: getDrawingsSvgsData(),
      };
    }

    if (templateType === "PURCHASE_ORDER") {
      return {
        ...getGenericOrderData({ order, templateType, company }),
        ...getCompanyData({ company, hasDrawings: true }),
        ...getProductsData(),
        ...getPurchaseOrderBaseData(),
        obtaining_method: order?.obtainingMethod || "",
        created_date: order?.createdDate || "",
        required_date: order?.requiredDate || "",
        accounting_name:
          company?.accountingName && company.accountingName !== "None"
            ? (`${company?.accountingName} Order #` ?? "")
            : "",
        drawings_svgs_data: getDrawingsSvgsData(),
      };
    }
  };
  return getDataByTemplateType(templateType);
};

export const getDeliveryRunSheetData = ({ vehicles, data }) => {
  return {
    ...getCompanyData({ company: data.company }),
    run_sheet_date: data.runSheetDate,
    vehicles: vehicles.map((v) => ({
      vehicle_uid: v.vehicleUid,
      vehicle_name: v.vehicleName,
      orders: v.orders.map((o) => ({
        ...getGenericOrderData({ order: o, templateType: "RUN_SHEET" }),
        ...getSalesOrderBaseData({
          order: o,
          customer: null,
        }),
        custom_fields: getCustomFieldsData({
          customFields: o.customFields,
          currencySymbol: data.company?.currencySymbol ?? "$",
        }),
        shipping_address_1: o?.shippingAddress?.address1 || "",
        shipping_address_2: o?.shippingAddress?.address2 || "",
        shipping_city: o?.shippingAddress?.city || "",
        shipping_state:
          getStateByCode({
            countryCode: o?.shippingAddress?.countryCode || "",
            stateCode: o?.shippingAddress?.state || "",
          })?.name ?? "",
        shipping_country: getCountryByCode(o?.shippingAddress?.countryCode || "")?.name ?? "",
        shipping_postcode: o?.shippingAddress?.postcode || "",
      })),
    })),
  };
};
